import { Fragment, useCallback, useRef, useEffect, useState } from 'react'
import { useRouter } from "next/router"
import Image from 'next/image'
import dayjs from 'dayjs'
import { styled } from '@mui/material'
import { useTranslation } from 'src/utils/i18n'
import { delFile, getLocalAndroidAll } from 'src/@api/dashboard'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import { androidLocalRefresh, localShooting } from 'src/@api/android'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'
import ContentLoading from 'src/components/ContentLoading'

// 自定义滚动条(div
const ScrollDivShow = styled('div')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const TakePhotos = ({ }) => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [intiData, setIntiData] = useState([]) // 原始数据
  const [showData, setShowData] = useState([]) // 展示的数据
  const [allDateDetail, setAllDateDetail] = useState([]) // 当前所有的时间图片date详情
  const [allDate, setAllDate] = useState([]) // 当前所有的日期点
  const [allTime, setAllTime] = useState([]) // 当前所有的时间点
  const [checkAll, setCheckAll] = useState(false) // 是否全选
  const [checkDateArr, setCheckDateArr] = useState([]) // 多选的日期
  const [checkArr, setCheckArr] = useState([]) // 多选的图片data
  const [showDelete, setShowDelete] = useState(false) // 显示删除弹窗
  const [clickData, setClickData] = useState({ index: -1, date: 0, url: '', position: 0 }) // 当前点击的图片数据 position(0:首张 1:中间 2:尾张)
  const [clickFlag, setClickFlag] = useState(false) // 当前是否为查看图片中
  const [fullShow, setFullShow] = useState(false) // 当前是否全屏查看图片
  const [hoverData, setHoverData] = useState(-1) // 当前悬浮的按钮
  const [hoverButton, setHoverButton] = useState(false) // 新增按钮悬浮状态
  const [camera, setCamera] = useState(1) // 前后摄像头(1为后摄2为前摄)
  const [isShooting, setIsShooting] = useState(false) // 是否正在拍摄
  const [shootingResult, setShootingResult] = useState(0) // 拍摄返回结果(1代表成功，2代表设备关机/没连上网，3代表摄像头被占用，4代表无权限)
  const [firstTake, setFirstTake] = useState(false) // 是否弹窗点击拍摄按钮提示框
  const [firstChoose, setFirstChoose] = useState(false) // 点击拍摄弹框单选框
  const [isShowError, setIsShowError] = useState(false) // 是否展示拍摄报错弹窗
  const [time] = useState(dayjs().format("HH:mm:ss")) // 当前时间
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  // 分页参数
  const [pagination, setPagination] = useState({
    reqNum: 100, // 单次查询数量
    nowOffset: 0, // 偏移量
    maxNum: false // 全部数据查询完毕
  })

  const groupingData = (arr) => {
    const groupMap = new Map() // 用 Map 提高查找效率
    const dateArr = []
    const timeArr = []

    arr.forEach(item => {
      const date = dayjs(item.date).format('YYYY-MM-DD')
      if (!groupMap.has(date)) {
        groupMap.set(date, [])
        dateArr.push(date) // 第一次遇到就记录日期
      }
      groupMap.get(date).push(item)
      timeArr.push(item.date) // 顺便收集所有 id
    })

    // 转成数组格式
    const groupArr = Array.from(groupMap, ([date, list]) => ({ date, list }))

    // 展示数据
    setShowData(groupArr)

    // 保存当前所有的时间点以及图片 id
    const allDateDetail = groupArr.map(item => ({
      date: item.date,
      list: item.list.map(i => i.date)
    }))
    setAllDateDetail(allDateDetail)

    // 保存所有日期点和 id 列表
    setAllDate(dateArr)
    setAllTime(timeArr)
  }

  const getData = useCallback(async (nowOffset, deviceUdid, { isCurrent, replace = false } = {}) => {
    const response = await getLocalAndroidAll({
      username: readUserEmail(),
      params: {
        category: 'REALTIME_PHOTO_CAPTURE',
        offset: nowOffset,
        limit: pagination.reqNum,
        deviceUdid
      }
    })

    if (isCurrent && !isCurrent()) return

    setIsLoading(false)
    if (response.data.contents == null) {
      setPagination(page => ({ ...page, maxNum: true }))

      return
    }

    const newArr = [...response.data.contents].sort((a, b) => b.date - a.date)

    if (replace) {
      setIntiData(newArr)
      groupingData(newArr)
      return
    }

    setIntiData(old => {
      const merged = [...old, ...newArr]
      groupingData(merged)

      return merged
    })
  }, [pagination.reqNum])

  // 设备切换时重置拍照列表并重新拉数
  useDeviceScopedInit(
    useCallback(
      (_, { context, isCurrent }) => {
        const deviceUdid = readLocalDeviceUdid(context)
        if (!deviceUdid) return

        setIntiData([])
        setShowData([])
        setAllDateDetail([])
        setAllDate([])
        setAllTime([])
        setCheckAll(false)
        setCheckDateArr([])
        setCheckArr([])
        setIsLoading(true)
        setPagination({ reqNum: 100, nowOffset: 0, maxNum: false })
        getData(0, deviceUdid, { isCurrent, replace: true })
      },
      [getData]
    )
  )

  const [scroll, setScroll] = useState(0)
  const dom = useRef(null)

  // 监听元素滚动条
  useEffect(() => {
    const handleScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = ((scrollTop / (scrollHeight - clientHeight)) * 100).toFixed(0)

        if (progress >= 75) setScroll(progress)
      }
    }

    handleScroll()

    if (dom.current) dom.current.addEventListener('scroll', handleScroll)

    return () => {
      if (dom.current) dom.current.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (scroll >= 100) {
      const nowOffset = pagination.nowOffset + pagination.reqNum
      setPagination(page => ({ ...page, nowOffset }))
      if (!pagination.maxNum) getData(nowOffset, readLocalDeviceUdid())
    }
  }, [scroll, getData, pagination.maxNum, pagination.nowOffset, pagination.reqNum])

  const changeCheckState = value => {
    if (typeof value === 'string') { // 全选指定日期

      const flag = checkDateArr.includes(value) // 判断是选择还是取消

      let arr = []
      showData.map(item => {
        if (item.date === value) arr = [...item.list.map(i => (i.date))]
      })

      let newCheckArr = []
      let newDateArr = []

      if (!flag) { // 选择状态
        newCheckArr = checkArr
        newDateArr = [...checkDateArr, value]

        if (checkArr.length <= 0) newCheckArr = arr
        else {
          const addArr = []
          arr.forEach(item => {
            if (!checkArr.includes(item)) addArr.push(item)
          })

          newCheckArr = [...newCheckArr, ...addArr]
        }
      } else { // 取消状态
        newCheckArr = [...checkArr.filter(i => !arr.includes(i))]
        newDateArr = [...checkDateArr.filter(i => i !== value)]
      }

      isCheckAll(newCheckArr, newDateArr)
      setCheckArr([...newCheckArr])
      setCheckDateArr([...newDateArr])
    }
    else if (typeof value === 'number') {
      const flag = checkArr.includes(value) // 判断是选择还是取消

      // 获取点击图片的当天所有时间点跟id
      let detail = null
      allDateDetail.forEach(item => {
        if (item.list.includes(value)) detail = item
      })

      // 最新的选择列表
      const newCheckArr = !flag ? [...checkArr, value] : [...checkArr.filter(i => i !== value)]

      // 判断是否全选
      let allCheck = true
      detail.list.forEach(item => {
        if (!newCheckArr.includes(item)) allCheck = false
      })

      let newDateArr = []

      // 更新当天是否全选
      if (allCheck) newDateArr = [...checkDateArr, detail.date]
      else newDateArr = [...checkDateArr.filter(i => i !== detail.date)]

      isCheckAll(newCheckArr, newDateArr)
      setCheckArr([...newCheckArr])
      setCheckDateArr([...newDateArr])
    }
  }

  // 点击全选按钮
  const handleCheckAllClick = () => {

    if (allDateDetail.length === 0) return

    let dateArr = []
    let timeArr = []

    if (!checkAll) {
      dateArr = allDate
      timeArr = allTime
    }

    setCheckDateArr([...dateArr])
    setCheckArr([...timeArr])
    setCheckAll(!checkAll)
  }

  // 判断是否改变全选按钮
  const isCheckAll = (checkArr, checkDateArr) => {
    if ((checkArr.length !== 0 || checkDateArr.length !== 0) && isArrayEqual(checkArr, allTime) && isArrayEqual(checkDateArr, allDate)) setCheckAll(true)
    else setCheckAll(false)
  }

  // 对比两个数组内容是否一致
  const isArrayEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false // 长度不同，直接返回 false
    const sortedArr1 = [...arr1].sort() // 对数组进行排序（不会改变原数组）
    const sortedArr2 = [...arr2].sort()

    return sortedArr1.every((value, index) => value === sortedArr2[index]) // 比较每一项
  }

  // 删除图片
  const deleteData = async () => {
    if (allDateDetail.length === 0) return

    const res = await delFile({
      username: readUserEmail(),
      params: {
        category: 'REALTIME_PHOTO_CAPTURE',
        deviceUdid: readLocalDeviceUdid(),
        idList: checkArr
      }
    })
    if (res.data.status) {
      const newShowData = showData.map(item => ({
        ...item,
        list: [...item.list]
      })).filter(i => !checkDateArr.includes(i.date))

      newShowData.forEach(item => {
        item.list = item.list.filter(i => !checkArr.includes(i.date))
      })

      setShowData([...newShowData])

      const newAllData = allDateDetail.map(item => ({
        ...item,
        list: [...item.list]
      })).filter(i => !checkDateArr.includes(i.date))

      newAllData.forEach(item => {
        item.list = item.list.filter(i => !checkArr.includes(i))
      })

      const newInitData = intiData.filter(i => !checkArr.includes(i.date))
      setIntiData(newInitData)

      setAllDateDetail([...newAllData])
      setCheckAll(false)

      setAllDate(old => (old.filter(i => !checkDateArr.includes(i))))
      setAllTime(old => (old.filter(i => !checkArr.includes(i))))

      setCheckArr([])
      setCheckDateArr([])

      setShowDelete(true)
      setTimeout(() => {
        setShowDelete(false)
      }, 3000)
    }
  }

  //新增图片
  const addData = async () => {
    setIsShooting(true)
    const timestamp = Date.now()
    const newItem = { date: timestamp, thumb: false }

    setIntiData(prev => {
      const newData = [...prev, newItem].sort((a, b) => b.date - a.date)
      groupingData(newData)

      return newData
    })

    try {
      await androidLocalRefresh({
        device_id: readLocalDeviceUdid(),
        type: 47,
        limit: false,
        front: camera == 1 ? false : true,
        timing: 0
      })

      // 轮询接口
      const result = await pollLocalShooting()
      const message = result?.data?.message ?? ""

      if (!message) {
        setShootingResult(2)
      } else if (message.includes("Shooting in progress")) {
        setShootingResult(1)
      } else if (message.includes("The target phone")) {
        setShootingResult(3)
      } else if (message.includes("Camera permission")) {
        setShootingResult(4)
      }

    } catch (err) {
      console.error("轮询拍照结果失败：", err)
      setShootingResult(2) // 设置一个超时或失败状态
    } finally {
      setIntiData(prev => {
        const filteredData = prev.filter(item => item.thumb !== false)
        groupingData(filteredData)

        return filteredData
      })
      setIsShooting(false)
      setIsShowError(true)
    }
  }

  // 轮询函数
  async function pollLocalShooting() {
    return new Promise((resolve, reject) => {
      const interval = 2000 // 每 2 秒轮询
      const timeout = 20000 // 20 秒超时
      let timer, timeoutTimer

      async function check() {
        try {
          let result = await localShooting({
            deviceID: readLocalDeviceUdid(),
            timing: false,
            called: "takephotos"
          })

          if (result?.data && result?.data?.message) {
            clearInterval(timer)
            clearTimeout(timeoutTimer)
            resolve(result)
          }
        } catch (err) {
          clearInterval(timer)
          clearTimeout(timeoutTimer)
          reject(err)
        }
      }

      check() // 立即执行一次
      timer = setInterval(check, interval) // 每 2 秒执行一次

      // 超时处理
      timeoutTimer = setTimeout(() => {
        clearInterval(timer)
        reject(new Error("轮询超时：20秒内未收到完成状态"))
      }, timeout)
    })
  }

  // 监听现有图片数量
  useEffect(() => {
    isCheckAll(checkArr, checkDateArr)
  }, [allDate, allTime])

  // 切换当前显示的图片
  const changeClickData = type => {
    const index = clickData.index

    if (index === -1) return

    let newIndex = 0
    if (type === 0) newIndex = index - 1 // 上一张
    else newIndex = index + 1 // 下一张

    const { date, thumb } = intiData[newIndex]

    setClickData({ index: newIndex, date, url: thumb.url, position: newIndex === 0 ? 0 : newIndex === intiData.length - 1 ? 2 : 1 })
  }

  // 当 fullShow 变为 false 时，重置屏幕缩放
  useEffect(() => {
    const metaTag = document.querySelector('meta[name="viewport"]')

    if (!metaTag) return

    if (!fullShow) { // 锁定缩放
      metaTag.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
      )
    } else { // 恢复默认（允许缩放）
      metaTag.setAttribute(
        'content',
        'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5, user-scalable=yes'
      )
    }
  }, [fullShow])

  // 获取当前语种的dayjs
  useEffect(() => {
    const handleLocale = async () => {
      switch (locale) {
        case 'fr':
          await import('dayjs/locale/fr')
          dayjs.locale('fr')
          break
        case 'jp':
          await import('dayjs/locale/ja')
          dayjs.locale('ja')
          break
        case 'es':
          await import('dayjs/locale/es')
          dayjs.locale('es')
          break
        case 'de':
          await import('dayjs/locale/de')
          dayjs.locale('de')
          break
        case 'it':
          await import('dayjs/locale/it')
          dayjs.locale('it')
          break
        case 'pt':
          await import('dayjs/locale/pt')
          dayjs.locale('pt')
          break
        case 'kr':
          await import('dayjs/locale/ko')
          dayjs.locale('ko')
          break
        case 'ar':
          await import('dayjs/locale/ar')
          dayjs.locale('ar')
          break
        case 'tr':
          await import('dayjs/locale/tr')
          dayjs.locale('tr')
          break
        default:
          await import('dayjs/locale/en')
          dayjs.locale('en')
      }
    }
    handleLocale()
  }, [locale])

  useEffect(() => {
    const title = t('Take Photos')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  return (
    <Fragment>
      <div className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-hidden relative touch-none'>

        {/* 全选&拍摄&删除提示 */}
        <div className='w-full p-[23px_35px_22px_32px] smj:p-[15px_12px_22px_16px] [border-bottom:1px_solid_#E2E2E4] sticky top-0 bg-[#FFFFFF]'>
          <div className='flex justify-between items-center flex-wrap gap-y-[12px]'>

            {/* left */}
            <div className='smj:flex-grow flex items-center select-none'>
              <label className={`flex items-center  ${allDateDetail.length === 0 ? 'cursor-no-drop' : 'cursor-pointer'}`} onClick={handleCheckAllClick}>
                <div
                  className={`w-[20px] h-[20px] flex justify-center items-center
                            ${checkAll ? 'bg-[#2A99FF] active:bg-[#247FDB]' : '[border:1px_solid_#DCDCDC] active:[border:1px_solid_#8D8D8D]'} rounded-[3px]`}
                >
                  <Image src='/image/photos/tick.svg' alt='tick' width={16} height={14} className={`w-[16.02px] h-[13.79px] ${!checkAll ? 'hidden' : ''}`} />
                </div>

                <p className='text-[18px] text-[#000000] font-[Poppins] font-[600] leading-[27px] ml-[12px] mr-[32px] smj:mx-[10px]'>{t('All Select')}</p>
              </label>
              <Image src='/image/photos/delete.svg' alt='trash-basket' width={20} height={21} className={`w-[20.5px] h-[21.5px] ${checkArr.length === 0 && checkDateArr.length === 0 ? 'cursor-no-drop' : 'cursor-pointer'}`}
                onClick={() => (checkArr.length !== 0 || checkDateArr.length !== 0) && deleteData()} />
            </div>

            {/* right */}
            <button
              className={`w-auto flex justify-center items-center p-[7px_20px] smj:p-[7px_14px_7px_14px] rounded-[8px] text-[14px] text-[#FFFFFF] leading-[21px] relative
              disabled:cursor-not-allowed ${isShooting ? 'bg-[#00000026]' : 'bg-[#2A99FF]'}`}
              disabled={isShooting}
              onClick={() => {
                if (localStorage.getItem('isTakePhotosPrompt')) {
                  addData()
                } else {
                  setFirstTake(true)
                }
              }}
            >
              {t('Shoot Now')}
              <Image src='/image/photos/white-arrow.svg' alt='white-arrow' width={20} height={20} className={`w-[20px] h-[20px] ml-[10px] transition-transform duration-150 ease-linear ${hoverButton ? 'rotate-[180deg]' : 'rotate-0'}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setHoverButton(!hoverButton)
                }} />
              <div className={`absolute top-[37px] w-full h-auto p-3 bg-white rounded-[8px] [box-shadow:0px_0px_12px_0px_#00000026] ${hoverButton || 'hidden'}`}>
                <div className='flex justify-between items-center'
                  onClick={(e) => {
                    e.stopPropagation()
                    setCamera(1)
                    setHoverButton(false)
                  }}>
                  <p className='text-[13px] leading-[21px] text-[#00000099]'>{t('Rear Camera')}</p>
                  <Image src={camera == 1 ? '/image/photos/choose.svg' : '/image/photos/no-choose.svg'} width={14} height={14} alt='' />
                </div>
                <div className='flex justify-between items-center mt-3'
                  onClick={(e) => {
                    e.stopPropagation()
                    setCamera(2)
                    setHoverButton(false)
                  }}>
                  <p className='text-[13px] leading-[21px] text-[#00000099]'>{t('Front Camera')}</p>
                  <Image src={camera == 2 ? '/image/photos/choose.svg' : '/image/photos/no-choose.svg'} width={14} height={14} alt='' />
                </div>
              </div>
            </button>
          </div>

          {/* 删除提示 */}
          <div className={`w-auto p-[15px_12px] [box-shadow:0px_0px_12px_0px_#00000061] bg-[#FFFFFF] rounded-[8px] absolute left-[50%] -translate-x-[50%] z-[5254] transition-[top] duration-150 ease-linear ${!showDelete ? '-top-[101%]' : 'top-[50%]'}`}>
            <p className='text-nowrap'>{t('Deleted successfully')}</p>
          </div>
        </div>

        {/* 图片展示部分 */}
        <ScrollDivShow ref={dom} className={`!h-[calc(var(--vh2)*100-296px)] smj:!h-[calc(var(--vh2)*100-163px)] overflow-auto pb-[20px] bg-white`}>
          {showData.map((item, index) => (
            <div key={index} className={`${index === 0 ? 'mt-[30px] smj:mt-[24px]' : 'mt-[40px] smj:mt-[26px]'} ml-[32px] smj:mx-[20px]`}>

              {/* 选择当天&当前日期 */}
              <div className='flex items-center mb-[31px]'>
                <div
                  className={`w-[20px] h-[20px] flex justify-center items-center rounded-[3px] cursor-pointer select-none
                                ${checkDateArr.includes(item.date) ? 'bg-[#2A99FF] active:bg-[#247FDB]' : '[border:1px_solid_#DCDCDC] active:[border:1px_solid_#8D8D8D]'}`}
                  onClick={() => changeCheckState(item.date)}
                >
                  <Image src='/image/photos/tick.svg' alt='tick' width={16} height={14} className={`w-[16.02px] h-[13.79px] ${!checkDateArr.includes(item.date) ? 'hidden' : ''}`} />
                </div>

                <Image src='/image/photos/date.svg' alt='calendar' width={24} height={24} className='w-[24px] h-[24px] ml-[10px] mr-[12px]' />
                <p className='text-[16px] text-[#333333] font-[Poppins] font-[600] leading-[24px]'>{dayjs(item.date).format('MMM DD,YYYY')}</p>
              </div>

              {/* 当天所有图片展示 */}
              <ul className='flex flex-wrap gap-x-[30px] gap-y-[21px] smj:grid smj:grid-cols-2 smj:gap-x-[25px] items-center'>
                {item.list.map((item2, i) => (
                  <li key={i}>
                    {item2.thumb !== false ?
                      <div className='bg-white rounded-[8px] overflow-hidden [box-shadow:0px_0px_15px_0px_#0000001F] relative'>
                        <Image src={'https://cos.spyx.com/' + item2.thumb.url} alt={''} width={160} height={160} className='w-[160px] h-[160px] smj:w-full smj:h-[155px] object-cover cursor-pointer' onClick={() => {
                          const nowIndex = intiData.findIndex(item => item.date === item2.date)
                          setClickData({ index: nowIndex, date: item2.date, url: item2.thumb.url, position: nowIndex === 0 ? 0 : nowIndex === intiData.length - 1 ? 2 : 1 })
                          setClickFlag(true)
                        }} />
                        <div className='flex gap-x-[8px] ml-[8px] my-[10px]'>
                          <div
                            className={`w-[20px] h-[20px] flex justify-center items-center rounded-[3px] cursor-pointer select-none
                                        ${checkArr.includes(item2.date) ? 'bg-[#2A99FF] active:bg-[#247FDB]' : '[border:1px_solid_#DCDCDC] active:[border:1px_solid_#8D8D8D]'}`}
                            onClick={() => changeCheckState(item2.date)}
                          >
                            <Image src='/image/photos/tick.svg' alt='tick' width={16} height={14} className={`w-[16.02px] h-[13.79px] ${!checkArr.includes(item2.date) ? 'hidden' : ''}`} />
                          </div>
                          <p className='text-[14px] text-[#666666] font-[Poppins] font-[400] leading-[21px]'>{dayjs(item2.date).format('HH:mm:ss')}</p>
                        </div>
                      </div>
                      :
                      <div className='bg-white rounded-[8px] overflow-hidden [box-shadow:0px_0px_15px_0px_#0000001F] relative'>
                        <div className='w-[160px] h-[160px] smj:w-full smj:h-[155px] bg-[#EBEBEB] flex flex-col justify-center items-center'>
                          <Image src={'/image/photos/shooting.gif'} width={50} height={50} alt='shooting' className='' />
                          <p className='mt-4 text-[12px] leading-[18px] text-[#000000B2] text-center'>{t('Taking photo')}....</p>
                        </div>
                        <div className='flex gap-x-[8px] ml-[8px] my-[10px]'>
                          <div className={`w-[20px] h-[20px] flex justify-center items-center rounded-[3px] [border:1px_solid_#DCDCDC]`}></div>
                          <p className='text-[14px] text-[#666666] font-[Poppins] font-[400] leading-[21px]'>{time}</p>
                        </div>
                      </div>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ScrollDivShow>

        {/* 图片查看遮罩层 */}
        <div className={`w-full h-full ${!clickFlag ? 'hidden' : ''} bg-[#00000033] fixed inset-0 z-[52]`} onClick={() => setClickFlag(false)}>
          <Image src='/image/photos/tips-x.svg' alt='tips-x' width={30} height={30} className='w-[30px] h-[30px] absolute top-[145px] right-[93px] smj:top-[90px] smj:right-[30px] cursor-pointer' />
        </div>

        {/* 图片查看 */}
        <div className={`h-full max-h-[90%] max-w-[80%] smj:w-[75%] flex justify-center items-center ${!clickFlag ? 'hidden' : ''} absolute top-[50%] -translate-y-[50%] left-[50%] -translate-x-[50%] z-[54]`} onClick={() => setClickFlag(false)}>
          <Image src={'https://cos.spyx.com/' + clickData.url} alt={''} width={100} height={100} className='w-full h-full smj:h-auto object-contain' onClick={e => {
            e.stopPropagation()
            if (window.innerWidth <= 990) setFullShow(true)
          }} />

          {/* 上一张 */}
          <div className='absolute w-[52px] h-[52px] smj:w-[30px] smj:h-[30px] top-[50%] -translate-y-[50%] -left-[58px] smj:-left-[40px] cursor-pointer'
            onMouseOver={() => setHoverData(0)}
            onMouseOut={() => setHoverData(-1)}
            onClick={e => {
              e.stopPropagation()
              clickData.index > 0 && changeClickData(0)
            }}>
            <div className={`w-[52px] h-[52px] smj:w-[30px] smj:h-[30px] !bg-white rounded-[50%] flex justify-center items-center
                 ${clickData.position === 0 ? '[border:1px_#C1C6D7_solid]' : hoverData === 0 ? 'shadow-[0px_0px_3px_gray]' : ''}`}>
              <div className={`w-[15px] h-[15px] smj:w-[10px] smj:h-[10px] relative left-[5%] [clip-path:polygon(0%_0%,15%_0%,15%_85%,100%_85%,100%_100%,0%_100%)] rotate-[45deg]
                   ${clickData.position === 0 ? 'bg-[#D8E0F2]' : hoverData === 0 ? 'bg-[#2A99FF]' : ' bg-[#282828]'}`}></div>
            </div>
          </div>

          {/* 下一张 */}
          <div className='absolute w-[52px] h-[52px] smj:w-[30px] smj:h-[30px] top-[50%] -translate-y-[50%] -right-[58px] smj:-right-[40px] cursor-pointer'
            onMouseOver={() => setHoverData(1)}
            onMouseOut={() => setHoverData(-1)}
            onClick={e => {
              e.stopPropagation()
              clickData.index < intiData.length - 1 && changeClickData(1)
            }}>
            <div className={`w-[52px] h-[52px] smj:w-[30px] smj:h-[30px] !bg-white rounded-[50%] flex justify-center items-center
                 ${clickData.position === 2 || intiData.length === 1 ? '[border:1px_#C1C6D7_solid]' : hoverData === 1 ? 'shadow-[0px_0px_3px_gray]' : ''}`}>
              <div className={`w-[15px] h-[15px] smj:w-[10px] smj:h-[10px] relative -left-[5%] [clip-path:polygon(0%_0%,15%_0%,15%_85%,100%_85%,100%_100%,0%_100%)] -rotate-[135deg]
                   ${clickData.position === 2 || intiData.length === 1 ? 'bg-[#D8E0F2]' : hoverData === 1 ? 'bg-[#2A99FF]' : ' bg-[#282828]'}`}></div>
            </div>
          </div>
        </div>

        {/* 点击拍摄按钮弹窗提示 */}
        <div className={`w-full h-full ${firstTake || 'hidden'} bg-[#00000033] fixed inset-0 smjs:left-[150px] z-[52]`}>
          <div className='mx-auto w-[430px] smj:w-[88%] h-auto bg-white rounded-[16px] p-[50px] smj:p-6 mt-[350px] smj:mt-[70%] relative'>
            <p className='font-[16px] leading-[24px] text-[#000000]'>
              {t('Do you want to take the photo now using the target phones')} {camera == 1 ? t('rear camera') : t('front camera')}
            </p>
            <div className='flex items-center mt-[27px] mb-[32px] smj:mt-[12px] smj:mb-[24px]'>
              <button className={`w-[20px] h-[20px] flex justify-center items-center rounded-[3px] cursor-pointer select-none
                ${firstChoose ? 'bg-[#2A99FF] active:bg-[#247FDB]' : '[border:1px_solid_#DCDCDC] active:[border:1px_solid_#8D8D8D]'}`}
                onClick={() => setFirstChoose(!firstChoose)}>
                <Image src='/image/photos/tick.svg' alt='tick' width={16} height={14} className={`w-[16.02px] h-[13.79px] ${!firstChoose ? 'hidden' : ''}`} />
              </button>
              <p className='text-[14px] leading-[21px] text-[#626262] ml-[10px]'>{t('Always take the photo without asking')}</p>
            </div>
            <div className='flex justify-center items-center'>
              <button className='w-auto h-[36px] px-[40px] bg-[#D9D9D9] rounded-[20px] font-[16px] leading-[36px] font-[500] text-white mr-[26px]'
                onClick={() => setFirstTake(false)}>
                {t('No')}
              </button>
              <button className='w-auto h-[36px] px-[40px] bg-[#FFCF55] rounded-[20px] font-[16px] leading-[36px] font-[500] text-[#1B1C2B] hover:bg-[#f0b933] cursor-pointer'
                onClick={() => {
                  setFirstTake(false)
                  if (firstChoose) localStorage.setItem('isTakePhotosPrompt', true)
                  addData()
                }}
              >
                {t('Yes')}
              </button>
            </div>
          </div>
        </div>

        {/* 拍摄轮询结果弹窗提示 */}
        <div className={`w-full h-full ${isShowError || 'hidden'} bg-[#00000033] fixed inset-0 smjs:left-[150px] z-[52]`}>
          <div className='mx-auto w-[430px] smj:w-[88%] h-auto bg-white rounded-[16px] p-[50px] smj:p-6 mt-[350px] smj:mt-[70%] relative'>
            <p className='font-[16px] leading-[24px] text-[#000000] font-[600]'>
              {shootingResult == 1 ? t('Photo capture successfully') : t('Photo capture failed')}
            </p>
            <p className='text-[16px] leading-[24px] text-[#000000CC] mt-[12px]'>
              {shootingResult == 1 ? t('Shooting in progress will upload shooting data later') : shootingResult == 2 ? t('The target phone is powered off or is not connecting to the internet') : shootingResult == 3 ? t('The target phones camera is currently in use') : shootingResult == 4 ? t('Camera permission is not enabled on the target phone') : ''}
            </p>
            <p className='text-[16px] leading-[24px] text-[#000000CC] mt-[8px]'>
              {shootingResult == 1 ? t('Please wait about 15 minutes to refresh the page') : (shootingResult == 2 || shootingResult == 3) ? t('Please try again later') : shootingResult == 4 ? t('Please enable it and try again') : ''}
            </p>
            <button className='w-auto flex mx-auto h-[36px] px-[40px] bg-[#FFCF55] rounded-[20px] font-[16px] leading-[36px] font-[500] text-[#1B1C2B] hover:bg-[#f0b933] cursor-pointer mt-[24px]'
              onClick={() => setIsShowError(false)}>
              {t('OK')}
            </button>
          </div>
        </div>
      </div>

      {/* 图片全屏显示 */}
      <div className={`w-full h-full flex justify-center items-center bg-black absolute top-0 left-0 z-[58] ${!fullShow ? 'hidden' : 'smjs:hidden'}`} onClick={() => setFullShow(false)}>
        <Image src={'https://cos.spyx.com/' + clickData.url} alt={''} width={100} height={100} className='w-full h-full object-contain' />
      </div>

      {/* 没数据提示 */}
      {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(intiData.length > 0 || isLoading) ||
        <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
          {t('It appears theres no data to show here yet')}<br />
          {t('If data exists it may take up to 1 hour to download and appear')}<br />
          {t('We suggest checking back in a little while')}
        </div>}
    </Fragment>
  )
}

export default TakePhotos